import { useCallback, useRef, useState } from 'react'
import type { TtsVoice } from '../utils/ttsVoices'

/**
 * useVoicePreview — проигрывание превью голоса (preview_url ElevenLabs).
 *
 * Один источник логики для десктоп-дропдауна (TtsVoicePicker), мобильной шторки
 * (VoiceRow в MobileSettingsSheet) и «Диалога». Превью может не быть или ссылка
 * может не открыться → onerror тихо сбрасывает, ▶ просто ничего не играет.
 */
export function useVoicePreview() {
	const [playing, setPlaying] = useState<string | null>(null)
	const audioRef = useRef<HTMLAudioElement | null>(null)

	const stop = useCallback(() => {
		if (audioRef.current) {
			audioRef.current.pause()
			audioRef.current = null
		}
		setPlaying(null)
	}, [])

	const toggle = useCallback((voice: TtsVoice) => {
		if (audioRef.current) audioRef.current.pause()
		if (playing === voice.id) {
			audioRef.current = null
			setPlaying(null)
			return
		}
		if (!voice.sample) {
			audioRef.current = null
			setPlaying(null)
			return
		}
		const a = new Audio(voice.sample)
		audioRef.current = a
		a.onended = () => setPlaying(null)
		a.onerror = () => setPlaying(null)
		a.play().then(() => setPlaying(voice.id)).catch(() => setPlaying(null))
	}, [playing])

	return { playing, toggle, stop }
}
